import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Loading } from '@/components/organisms/Loading';
import { getRandomHint } from '@/components/molecules/LoadingHints';

interface LoadingState {
  isLoading: boolean;
  message?: string;
  hint?: string;
  bgColor?: string;
  textColor?: string;
  fullPage?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

interface LoadingContextType {
  loadingState: LoadingState;
  setLoading: (loading: boolean, options?: Partial<Omit<LoadingState, 'isLoading'>>) => void;
  showLoading: (options?: Partial<Omit<LoadingState, 'isLoading'>>) => void;
  hideLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

const defaultLoadingState: LoadingState = {
  isLoading: false,
  message: 'Loading...',
  fullPage: true,
  size: 'md',
};

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loadingState, setLoadingState] = useState<LoadingState>(defaultLoadingState);

  const setLoading = useCallback(
    (isLoading: boolean, options?: Partial<Omit<LoadingState, 'isLoading'>>) => {
      setLoadingState((prev) => ({
        ...prev,
        isLoading,
        ...(isLoading
          ? {
              message: options?.message || prev.message || 'Loading...',
              hint: options?.hint !== undefined ? options.hint : prev.hint || getRandomHint(),
              bgColor: options?.bgColor || prev.bgColor,
              textColor: options?.textColor || prev.textColor,
              fullPage: options?.fullPage !== undefined ? options.fullPage : prev.fullPage ?? true,
              size: options?.size || prev.size || 'md',
            }
          : {}),
      }));
    },
    []
  );

  const showLoading = useCallback(
    (options?: Partial<Omit<LoadingState, 'isLoading'>>) => {
      setLoading(true, options);
    },
    [setLoading]
  );

  const hideLoading = useCallback(() => {
    setLoading(false);
  }, [setLoading]);

  const value: LoadingContextType = {
    loadingState,
    setLoading,
    showLoading,
    hideLoading,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {loadingState.isLoading && (
        <div className="fixed inset-0 z-[9999]">
          <Loading
            message={loadingState.message}
            fullPage={true}
            size={loadingState.size}
            bgColor={loadingState.bgColor}
            textColor={loadingState.textColor}
            hint={loadingState.hint}
          />
        </div>
      )}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

